import logger from '../../utils/logger';
/**
 * InsuranceCaptureModal
 * ---------------------
 * Self-contained modal for adding an insurance policy to a registered patient:
 * scan an insurance card (OCR pre-fill + GCS image capture) and/or type the
 * fields manually, then POST to /patients/aboutPatientBilling. The scanned card
 * image paths are persisted (insurance_card_images) alongside the new policy.
 *
 * Used by the Payments page when an appointment is set to bill insurance but the
 * patient has no insurance on record. Reuses the InsuranceCardScanner + the same
 * billing endpoint as the patient profile.
 */

import React, { useState } from 'react';
import { InputMask } from '@react-input/mask';
import InsuranceCardScanner from './InsuranceCardScanner';

const EMPTY = {
  insurance_name: '', insurance_address: '', insurance_city: '',
  insurance_state: '', insurance_zip: '', insurance_phone: '',
  member_id: '', group_number: '', effective_date: '',
  termination_date: '', subscriber_name: '', subscriber_dob: '', relationship: '',
};

const InsuranceCaptureModal = ({ risPatientsId, user, type, onClose, onSaved }) => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [form, setForm] = useState(EMPTY);
  const [cardImages, setCardImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // OCR pre-fill: only non-zero-confidence fields overwrite, so manual edits stick.
  const handleScannerFields = (uid, fields, fieldDetails) => {
    setForm((prev) => {
      const updated = { ...prev };
      for (const [key, value] of Object.entries(fields || {})) {
        if (value && fieldDetails?.[key]?.confidence > 0 && key in updated) {
          updated[key] = value;
        }
      }
      return updated;
    });
  };

  const handleScannerImages = (uid, images) => {
    if (!images || !images.length) return;
    setCardImages((prev) => [...prev, ...images]);
  };

  const handleSubmit = async () => {
    if (!form.insurance_name || !form.member_id || !form.subscriber_name || !form.relationship) {
      setError('Please fill in all required fields: Insurance Name, Member ID, Subscriber Name, and Relationship.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await fetch(
        `${BASE_URL}/patients/aboutPatientBilling?ris_patients_id=${risPatientsId}&user=${user}&type=${type}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, card_images: cardImages }),
        }
      );
      if (response.ok) {
        if (onSaved) await onSaved();
        onClose();
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.msg || 'Failed to add insurance plan.');
      }
    } catch (err) {
      logger.error('Error adding insurance:', err);
      setError('An error occurred while adding insurance.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group-modal-overlay" onClick={onClose}>
      <div className="group-modal" style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3>Add Insurance</h3>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
          Scan the patient's insurance card to auto-fill the fields, or enter them manually.
          The card images are stored on the patient record.
        </p>

        <InsuranceCardScanner
          index="payments"
          uid="payments"
          risPatientId={risPatientsId}
          onFieldsExtracted={handleScannerFields}
          onImagesUploaded={handleScannerImages}
        />

        <div style={styles.grid}>
          <div style={styles.field}>
            <label style={styles.label}>Insurance Name <span style={styles.req}>*</span></label>
            <input type="text" name="insurance_name" value={form.insurance_name} onChange={handleChange} placeholder="e.g. Blue Cross Blue Shield" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Insurance Phone</label>
            <InputMask mask="(XXX) XXX-XXXX" replacement={{ X: /\d/ }} type="text" name="insurance_phone" value={form.insurance_phone} onChange={handleChange} placeholder="(000) 000-0000" style={styles.input} />
          </div>
          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Street Address</label>
            <input type="text" name="insurance_address" value={form.insurance_address} onChange={handleChange} placeholder="123 Main St" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>City</label>
            <input type="text" name="insurance_city" value={form.insurance_city} onChange={handleChange} placeholder="City" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>State</label>
            <input type="text" name="insurance_state" value={form.insurance_state} onChange={handleChange} placeholder="State" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Zip</label>
            <InputMask mask="XXXXX" replacement={{ X: /\d/ }} type="text" name="insurance_zip" value={form.insurance_zip} onChange={handleChange} placeholder="00000" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Member ID <span style={styles.req}>*</span></label>
            <input type="text" name="member_id" value={form.member_id} onChange={handleChange} placeholder="Member ID" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Group Number</label>
            <input type="text" name="group_number" value={form.group_number} onChange={handleChange} placeholder="Group Number" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Effective Date</label>
            <input type="date" name="effective_date" value={form.effective_date} onChange={handleChange} style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Termination Date</label>
            <input type="date" name="termination_date" value={form.termination_date} onChange={handleChange} style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Subscriber Name <span style={styles.req}>*</span></label>
            <input type="text" name="subscriber_name" value={form.subscriber_name} onChange={handleChange} placeholder="Full Name" style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Subscriber DOB</label>
            <input type="date" name="subscriber_dob" value={form.subscriber_dob} onChange={handleChange} style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Relationship <span style={styles.req}>*</span></label>
            <select name="relationship" value={form.relationship} onChange={handleChange} style={styles.input}>
              <option value="">-- Select --</option>
              <option value="self">Self</option>
              <option value="parent">Parent</option>
              <option value="guardian">Guardian</option>
              <option value="spouse">Spouse</option>
              <option value="child">Child</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {error && <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 10 }}>{error}</p>}

        <div className="group-modal-actions">
          <button className="PrimaryButton" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save Insurance'}
          </button>
          <button className="group-modal-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginTop: 12,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#374151' },
  req: { color: '#ef4444' },
  input: {
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    width: '100%',
    boxSizing: 'border-box',
  },
};

export default InsuranceCaptureModal;
